import { useEffect, useState } from "react";
import { Mail, Send, ShoppingCart, Sparkles } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATE_STYLE = {
  abandoned:      { label: "Abandoned", bg: "bg-amber-50", text: "text-amber-700" },
  recovery_sent:  { label: "Recovery sent", bg: "bg-blue-50", text: "text-blue-700" },
  recovered:      { label: "Recovered", bg: "bg-emerald-50", text: "text-emerald-700" },
};

export default function AdminCarts() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sendingId, setSendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/carts");
      setCarts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendRecovery = async (email) => {
    setSendingId(email);
    try {
      const { data } = await api.post(`/admin/carts/${encodeURIComponent(email)}/recover`);
      if (data.sent) toast.success(`Recovery email sent to ${email}`);
      else toast.info(`Marked sent (email not configured — set RESEND_API_KEY to actually send)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSendingId(null); }
  };

  const visible = filter === "all" ? carts : carts.filter((c) => c.state === filter);
  const counts = carts.reduce((acc, c) => { acc[c.state] = (acc[c.state] || 0) + 1; return acc; }, {});
  const recovered = counts.recovered || 0;
  const abandoned = counts.abandoned || 0;
  const totalValue = carts.filter((c) => c.state !== "recovered").reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <PortalShell title="Abandoned carts" subtitle="Admin · Retention">
      <div className="grid sm:grid-cols-3 gap-4 mb-7">
        <div className="rounded-3xl bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-amber-600" /></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Open</span>
          </div>
          <div className="font-display text-4xl font-bold text-slate-900 mt-3">{abandoned}</div>
          <p className="text-sm text-slate-500 mt-1">Abandoned (not yet contacted)</p>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Sparkles className="w-5 h-5 text-emerald-600" /></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Won back</span>
          </div>
          <div className="font-display text-4xl font-bold text-slate-900 mt-3">{recovered}</div>
          <p className="text-sm text-slate-500 mt-1">Recovered (purchased after capture)</p>
        </div>
        <div className="rounded-3xl bg-slate-900 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center"><Mail className="w-5 h-5 text-cyan-400" /></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-400">Potential</span>
          </div>
          <div className="font-display text-4xl font-bold mt-3">${totalValue.toFixed(2)}</div>
          <p className="text-sm text-slate-300 mt-1">Open revenue at risk</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { value: "all", label: `All (${carts.length})` },
          { value: "abandoned", label: `Abandoned (${counts.abandoned || 0})` },
          { value: "recovery_sent", label: `Recovery sent (${counts.recovery_sent || 0})` },
          { value: "recovered", label: `Recovered (${counts.recovered || 0})` },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`admin-carts-filter-${f.value}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              filter === f.value ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"
            }`}
          >{f.label}</button>
        ))}
      </div>

      <div data-testid="admin-carts-table" className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading carts...</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No carts in this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Customer</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Test</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Value</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Last seen</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">State</th>
                  <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const sty = STATE_STYLE[c.state] || STATE_STYLE.abandoned;
                  return (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="p-4">
                        <div className="font-semibold text-slate-900">{c.name || "—"}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </td>
                      <td className="p-4 text-slate-700">{c.test_name}</td>
                      <td className="p-4 font-semibold text-slate-900">${c.amount.toFixed(2)}</td>
                      <td className="p-4 text-xs text-slate-500">{formatDateTimeAU(c.last_seen_at)}</td>
                      <td className="p-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${sty.bg} ${sty.text}`}>{sty.label}</span>
                      </td>
                      <td className="p-4 text-right">
                        {c.state !== "recovered" && (
                          <button
                            data-testid={`admin-cart-recover-${c.email}`}
                            onClick={() => sendRecovery(c.email)}
                            disabled={sendingId === c.email}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                          >
                            <Send className="w-3.5 h-3.5" /> {sendingId === c.email ? "Sending..." : c.recovery_sent_at ? "Resend" : "Send recovery"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
